import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useApiQuery, useCrud } from '../../hooks/useApi';
import { identityService } from '../../services/api';
import { fetchAllPages } from '../../utils/apiPagination';
import { unwrapList } from '../../utils/entityIds';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import ClientModal from '../../components/Identity/ClientModal';
import ClientTable from '../../components/Identity/ClientTable';
import ClientFilters from '../../components/Identity/ClientFilters';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../../contexts/TenantContext';
import { partyNoun } from '../../utils/tenantProfile';

const Clients = () => {
  const { t } = useTranslation('identity');
  const { tenant } = useTenant();
  const party = partyNoun(tenant);

  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    sortBy: 'name',
    sortOrder: 'asc'
  });

  const {
    useList,
    useCreate,
    useUpdate,
    useDelete
  } = useCrud('clients', identityService);

  const {
    data: clientsData,
    isLoading,
    error,
  } = useList();

  // O cliente só guarda o vínculo (empresa/pessoa); nome e documento vêm desses cadastros.
  const { data: companiesRaw = [] } = useApiQuery('identity-companies-clients-page', () =>
    fetchAllPages((params) => identityService.getCompanies(params))
  );
  const { data: personsRaw = [] } = useApiQuery('identity-persons-clients-page', () =>
    fetchAllPages((params) => identityService.getPersons(params))
  );

  const companiesById = useMemo(() => {
    const map = new Map();
    unwrapList(companiesRaw).forEach((c) => c?.id && map.set(String(c.id), c));
    return map;
  }, [companiesRaw]);

  const personsById = useMemo(() => {
    const map = new Map();
    unwrapList(personsRaw).forEach((p) => p?.id && map.set(String(p.id), p));
    return map;
  }, [personsRaw]);

  const rawClients = Array.isArray(clientsData?.data)
    ? clientsData.data
    : Array.isArray(clientsData)
    ? clientsData
    : [];

  const clients = useMemo(
    () =>
      rawClients.map((client) => {
        const company = client?.company_id ? companiesById.get(String(client.company_id)) : null;
        const person = client?.person_id ? personsById.get(String(client.person_id)) : null;
        const isCompany = Boolean(client?.company_id) || client?.client_type === 'legal_entity';
        return {
          ...client,
          name: client.name || company?.name || person?.name || client.username || '—',
          document: client.document || company?.cnpj || person?.cpf || '',
          cnpj: client.cnpj || company?.cnpj || '',
          cpf: client.cpf || person?.cpf || '',
          type: isCompany ? 'LEGAL_ENTITY' : 'INDIVIDUAL',
          address: client.address || company?.address || person?.address || '',
          city: client.city || company?.city || person?.city || '',
          state: client.state || company?.state || person?.state || '',
          zipCode: client.zipCode || company?.zip_code || person?.zip_code || '',
        };
      }),
    [rawClients, companiesById, personsById]
  );

  const createMutation = useCreate();
  const updateMutation = useUpdate();
  const deleteMutation = useDelete();

  const handleCreate = () => {
    setEditingClient(null);
    setShowModal(true);
  };

  const handleEdit = async (client) => {
    try {
      const { data } = await identityService.getClient(client.id);
      setEditingClient({ ...client, ...(data || {}) });
    } catch {
      setEditingClient(client);
    }
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(`Tem certeza que deseja excluir este ${party.one}?`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = async (data) => {
    try {
      if (editingClient) {
        await updateMutation.mutateAsync({ id: editingClient.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      setShowModal(false);
      setEditingClient(null);
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
    }
  };

  // Função para resolver o documento (CPF/CNPJ)
  const resolveDocument = (client) => {
    if (client.cpf) return client.cpf;
    if (client.cnpj) return client.cnpj;
    return client.document || '';
  };

  // Função para resolver o tipo de pessoa
  const resolvePersonType = (client) => {
    if (client.person_type) {
      return client.person_type === 'physical' ? 'INDIVIDUAL' : 'LEGAL_ENTITY';
    }
    if (client.personType) {
      return client.personType === 'physical' ? 'INDIVIDUAL' : 'LEGAL_ENTITY';
    }
    return client.type || 'INDIVIDUAL';
  };

  const filteredClients = Array.isArray(clients) ? clients.filter(client => {
    const matchesSearch = !filters.search || 
      (client.name && client.name.toLowerCase().includes(filters.search.toLowerCase())) ||
      (client.document && client.document.includes(filters.search));
    
    const matchesStatus = !filters.status || client.status === filters.status;
    const matchesType = !filters.type || client.type === filters.type;
    
    return matchesSearch && matchesStatus && matchesType;
  }) : [];

  const sortedClients = [...filteredClients].sort((a, b) => {
    const aValue = a[filters.sortBy] || '';
    const bValue = b[filters.sortBy] || '';
    
    if (filters.sortOrder === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  if (error) {
    return (
      <FormPageShell>
        <div className="alert alert-danger rounded-xl border border-red-200">
          <p className="text-red-800">{t('common:loadError')}</p>
        </div>
      </FormPageShell>
    );
  }

  return (
    <FormPageShell>
      <div className="space-y-6">
        <PageHeader
          title={party.Many}
          description={
            party.consultoria
              ? 'Cadastro de clientes (PF/PJ) vinculados à consultoria.'
              : 'Cadastro de requerentes (pessoas e empresas) do órgão.'
          }
          breadcrumbs={[
            { label: 'Início', to: getAppHomePath() },
            { label: 'Identidade', to: '/identity/clients' },
            { label: party.Many },
          ]}
        >
          <Can permission={P.IDENTITY_ACCESS}>
            <button type="button" onClick={handleCreate} className="btn btn-primary inline-flex items-center gap-2 shadow-sm">
              <Plus className="h-4 w-4" />
              Novo {party.one}
            </button>
          </Can>
        </PageHeader>

      <div className="data-list-card">
        <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
          <ClientFilters
            filters={filters}
            onFiltersChange={setFilters}
          />
        </div>

        <div className="p-5 sm:p-6">
          <ClientTable
            clients={sortedClients}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            resolveDocument={resolveDocument}
            resolvePersonType={resolvePersonType}
            onCreate={handleCreate}
          />
        </div>
      </div>
      </div>

      {showModal && (
        <ClientModal
          client={editingClient}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false);
            setEditingClient(null);
          }}
        />
      )}
    </FormPageShell>
  );
};

export default Clients;
